import { UsersListPageView } from './UsersList.view';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useBreadcrumbs } from '@hooks/useBreadcrumbs';
import { usePaginationAndSort } from '@restore/helpers';
import { NestedFilter } from '@components/NestedFilter/NestedFilter';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { SearchSuffix } from '@dynamika-shared/ui/Input/SearchSuffix';
import { useUsersFilters } from './useUsersFilters';
import { UserRequestArgs } from '@api/users';
import { useUsersListSlice } from '@restore/usersList/usersList.slice';
import { PossibleFilters } from '@restore/usersFilters/usersFilters.types';
import { useMakePagination } from '@restore/paginationAndSort/paginationAndSort.slice';
import useDebounceFn from '@shared/hooks/useDebouncedValue';

type OnSortColumnType = (name: string, dir?: string) => void;

export const UsersListPage = () => {
  useBreadcrumbs([['Users', 'usersAndLogs']]);

  const [fetchUsers, fetchUsersInstitution, data, loading, institutions] =
    useUsersListSlice((state, actions) => [
      actions.fetchUsersList,
      actions.fetchUsersInstitution,
      state.data,
      state.loading,
      state.institutions,
    ]);

  const {
    subFilters,
    filters,
    allFilters,
    toggleActiveFilter,
    toggleSelectedFilter,
    selectedFilters,
    filterOnClick,
    args,
    search,
    setSearch = () => null,
  } = useUsersFilters();

  const userArgs = args as UserRequestArgs;

  const { sortBy, currentPage, currentPageSize, setSortBy, setCurrentPage } =
    usePaginationAndSort('users');

  const [searchValue, setSearchValue] = useState<string>();

  const componentIsMounted: MutableRefObject<boolean> = useRef(false);

  const { run: delayedSearch } = useDebounceFn(nextValue => {
    setSearch(nextValue);
    setCurrentPage({ listName: 'users', newPage: 1 });
  }, 500);

  useEffect(() => {
    fetchUsers({
      sort: sortBy,
      size: currentPageSize,
      page: currentPage,
      ...(search && { filter: search }),
      ...userArgs,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, currentPageSize, search]);

  useEffect(() => {
    if (currentPage === 1 && componentIsMounted.current) {
      fetchUsers({
        sort: sortBy,
        size: currentPageSize,
        page: currentPage,
        ...(search && { filter: search }),
        ...userArgs,
      });
    } else {
      setCurrentPage({ listName: 'users', newPage: 1 });
    }
    if (!componentIsMounted?.current) {
      componentIsMounted.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [args.role, args.status]);

  const onSort: OnSortColumnType = (name, sortState) => {
    const newSort = sortState ? `${name},${sortState}` : undefined;
    setSortBy({ listName: 'users', newSortBy: newSort });
    fetchUsers({
      sort: newSort,
      page: currentPage,
      size: currentPageSize,
      ...(searchValue && { filter: searchValue }),
      ...userArgs,
    });
  };

  const refetchUsers = async () => {
    fetchUsers({
      sort: sortBy,
      size: currentPageSize,
      page: currentPage,
      ...(searchValue && { filter: searchValue }),
      ...userArgs,
    });
    fetchUsersInstitution(undefined);
  };

  useEffect(() => {
    fetchUsersInstitution(undefined);
  }, []);

  const pagination = useMakePagination('users', !!data?.content?.length);

  return (
    <div data-testid='UsersList' className='h-full'>
      <UsersListPageView
        onSort={onSort}
        sortState={sortBy}
        pagination={pagination}
        loading={loading}
        totalItems={data?.totalElements ?? 0}
        usersData={data?.content || []}
        institutions={institutions}
        refetchUsers={refetchUsers}
      >
        <NestedFilter
          onClick={filterOnClick}
          items={filters}
          subFilters={subFilters}
          allFilters={allFilters}
          closeTag={toggleActiveFilter}
          toggleSelectedFilter={(name: PossibleFilters, value: string) =>
            toggleSelectedFilter({ name, value })
          }
          selectedFilters={selectedFilters}
          overlayClassName={'w-[22.4rem]'}
        />
        <Input
          value={searchValue}
          onChange={e => {
            setSearchValue(e.target.value);
            delayedSearch(e.target.value);
          }}
          className='!mt-0 h-[4.8rem] min-w-[32rem] !rounded-[3.2rem] px-2'
          placeholder='Search by name or email'
          suffix={
            <SearchSuffix
              disabled={!searchValue}
              action={() => {
                setSearchValue('');
                setSearch('');
                setCurrentPage({ listName: 'users', newPage: 1 });
              }}
            />
          }
        />
      </UsersListPageView>
    </div>
  );
};
